// Game logic for the Buddha Game.
// This file handles user input (mouse/touch) to control rising and falling movement.
// Implements a configurable breathing system with a state machine for different patterns.
// Includes goal/progress tracking for the cloud destination and win condition.

import {
  startRising,
  startFalling,
  stopMovement,
  pulseRing,
  dimRing,
  pulseAura,
  dimAura,
  softShakeBuddha,
} from './animations';

// ============================================================================
// GOAL & PROGRESS CONFIGURATION
// ============================================================================
// These values control the goal height and progress mechanics.
// The Buddha must rise to the goal height to win.

export interface GoalConfig {
  /** Height the Buddha must reach to win (matches cloud position) */
  goalHeight: number;
  /** Progress gained per successful breath cycle */
  progressPerCycle: number;
  /** Progress lost on breathing failure (gentle penalty) */
  penaltyOnFailure: number;
  /** Minimum progress (cannot go below starting position) */
  minProgress: number;
  /** Additional bonus progress awarded per consecutive successful cycle (absolute units) */
  streakBonusPerSuccess: number;
  /** Maximum total bonus (caps streak bonus) */
  maxStreakBonus: number;
}
/**
 * Mark the session complete externally (e.g., Scene detected Buddha offscreen).
 * This stops breathing and marks the game as won without relying on cloud goal.
 */
export function markSessionComplete(): void {
  if (progressState.hasWon) return;
  progressState.hasWon = true;
  progressState.status = 'won';
  // Stop breathing when session completes
  stopBreathing();

  // eslint-disable-next-line no-console
  console.debug('progressState: session marked complete by Scene');

  if (progressState.onWin) progressState.onWin();
  // stop ambient audio when session completes
  stopAmbientSound();
  // freeze timer
  if (progressState.startTime) {
    progressState.accumulatedElapsed +=
      (Date.now() - progressState.startTime) / 1000;
    progressState.startTime = null;
  }
}

// Default goal configuration
const DEFAULT_GOAL_CONFIG: GoalConfig = {
  goalHeight: 5.5, // Slightly below cloud center for a nice landing
  progressPerCycle: 0.03, // Rise very slowly - 0.03 units per successful cycle
  penaltyOnFailure: 0.02, // Gentle penalty - 0.02 units on failure
  minProgress: 0, // Cannot go below starting position
  // When consecutive cycles are completed successfully the player receives
  // an extra small bonus per cycle to speed up progress and reward streaks.
  // E.g. first success adds 0.02 extra, second adds 0.04 (capped by maxStreakBonus)
  streakBonusPerSuccess: 0.02,
  maxStreakBonus: 0.06,
};

let goalConfig: GoalConfig = { ...DEFAULT_GOAL_CONFIG };

// ============================================================================
// PROGRESS STATE
// ============================================================================
// Tracks the player's progress toward the goal.

export type GameStatus = 'idle' | 'playing' | 'won' | 'paused';

interface ProgressState {
  /** Current progress (vertical offset toward goal) */
  currentProgress: number;
  /** Whether the player has won */
  hasWon: boolean;
  /** Current game status */
  status: GameStatus;
  /** Total perfect inhale+exhale cycles completed */
  successfulCycles: number;
  /** Callback for when a cycle is completed successfully */
  onCycleComplete: (() => void) | null;
  /** Callback for when the player wins */
  onWin: (() => void) | null;
  /** Callback for when a failure occurs */
  onFailure: (() => void) | null;
  /** Current consecutive successful cycle streak (resets on failure) */
  currentSuccessStreak: number;
  /** Timestamp when the game session started (ms since epoch) */
  startTime: number | null;
  /** Accumulated elapsed seconds from previous running periods (seconds) */
  accumulatedElapsed: number;
}

const progressState: ProgressState = {
  currentProgress: 0,
  hasWon: false,
  status: 'idle',
  successfulCycles: 0,
  onCycleComplete: null,
  onWin: null,
  onFailure: null,
  currentSuccessStreak: 0,
  startTime: null,
  accumulatedElapsed: 0,
};

function isGamePlaying(): boolean {
  return progressState.status === 'playing';
}

// ============================================================================
// INPUT STATE (declared early for use by getDebugInfo)
// ============================================================================
// Tracks whether input listeners have been set up and the current input state.

interface InputState {
  initialized: boolean;
  isPressed: boolean;
  cleanupFns: (() => void)[];
  // recent input events (press/release) for debug
  events: {
    timestamp: number;
    type: 'start' | 'end';
    source: 'mouse' | 'touch' | 'key';
    key?: string | null;
  }[];
}

const inputState: InputState = {
  initialized: false,
  isPressed: false,
  cleanupFns: [],
  events: [],
};

let strictHoldTimingEnabled = false;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

// ============================================================================
// INPUT TIMING WINDOW CONFIGURATION
// ============================================================================
// Input should be accepted only near the start of inhale/exhale.
// Base ratio is multiplied per level (configured via configureLevel).

const DEFAULT_VALID_WINDOW_RATIO = 0.25;
let inputWindowRatioMultiplier = 1.0;

function getValidWindowEndRatio(phase: BreathingPhase): number {
  if (phase !== 'inhale' && phase !== 'exhale') return 0;
  return clamp01(DEFAULT_VALID_WINDOW_RATIO * inputWindowRatioMultiplier);
}

function getCurrentPhaseProgressRatio(): number {
  const duration = getPhaseDuration(breathingState.currentPhase);
  if (duration <= 0) return 1;
  return clamp01(breathingState.phaseTimer / duration);
}

function setHoldPhaseStrictness(enabled: boolean) {
  strictHoldTimingEnabled = enabled;
}

// ============================================================================
// BREATHING PATTERN CONFIGURATION
// ============================================================================
// Configurable breathing pattern structure.
// Change these values to support different breathing ratios:
// - 4-0-4-0 (simple breathing)
// - 4-7-8-0 (relaxing breath)
// - 5-5-5-5 (box breathing)
// - 6-2-7-2 (custom patterns)
// - Kid-friendly patterns, etc.

export interface BreathingPattern {
  /** Duration of inhale phase in seconds */
  inhale: number;
  /** Duration of hold after inhale in seconds (can be 0) */
  holdAfterInhale: number;
  /** Duration of exhale phase in seconds */
  exhale: number;
  /** Duration of hold after exhale in seconds (can be 0) */
  holdAfterExhale: number;
}

// Default pattern: 4-0-4-0 (simple breathing without holds)
// To change the breathing pattern, simply modify this object.
let breathingPattern: BreathingPattern = {
  inhale: 4,
  holdAfterInhale: 0,
  exhale: 4,
  holdAfterExhale: 0,
};

// ============================================================================
// AUDIO SUPPORT
// ============================================================================
// Default audio URLs (can be customized later if needed)
const INHALE_AUDIO_URL =
  'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/audio/inhale.mp3';
const EXHALE_AUDIO_URL =
  'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/audio/exhale.mp3';
// Also play light voice tracks in sync with inhale/exhale
const INHALE_VOICE_AUDIO_URL =
  'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/audio/inhale-voice.mp3';
const EXHALE_VOICE_AUDIO_URL =
  'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/audio/exhale-voice.mp3';

let inhaleAudio: HTMLAudioElement | null = null;
let exhaleAudio: HTMLAudioElement | null = null;
let inhaleVoiceAudio: HTMLAudioElement | null = null;
let exhaleVoiceAudio: HTMLAudioElement | null = null;
let voiceAudioEnabled = true;
// Ambient/background audio that plays during an active game session
const AMBIENT_AUDIO_URL =
  'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/audio/starfield.mp3';

let ambientAudio: HTMLAudioElement | null = null;

function stopAndResetAudio(audioEl: HTMLAudioElement | null) {
  if (!audioEl) return;
  try {
    audioEl.pause();
  } catch (e) {
    void e;
  }
  try {
    audioEl.currentTime = 0;
  } catch (e) {
    void e;
  }
}

function setVoiceAudioEnabledSetting(enabled: boolean) {
  voiceAudioEnabled = enabled;
  if (!enabled) {
    stopAndResetAudio(inhaleVoiceAudio);
    stopAndResetAudio(exhaleVoiceAudio);
  }
}

function ensureAudioElements() {
  if (typeof window === 'undefined') return;
  try {
    if (!inhaleAudio && INHALE_AUDIO_URL) {
      inhaleAudio = new Audio(INHALE_AUDIO_URL);
      inhaleAudio.preload = 'auto';
      inhaleAudio.crossOrigin = 'anonymous';
      // main inhale music should be relatively low so voice is clear
      inhaleAudio.volume = 0.25;
    }
    if (!exhaleAudio && EXHALE_AUDIO_URL) {
      exhaleAudio = new Audio(EXHALE_AUDIO_URL);
      exhaleAudio.preload = 'auto';
      exhaleAudio.crossOrigin = 'anonymous';
      exhaleAudio.volume = 0.25;
    }
    if (!inhaleVoiceAudio && INHALE_VOICE_AUDIO_URL) {
      inhaleVoiceAudio = new Audio(INHALE_VOICE_AUDIO_URL);
      inhaleVoiceAudio.preload = 'auto';
      inhaleVoiceAudio.crossOrigin = 'anonymous';
      inhaleVoiceAudio.volume = 0.7; // voice should be prominent
    }
    if (!exhaleVoiceAudio && EXHALE_VOICE_AUDIO_URL) {
      exhaleVoiceAudio = new Audio(EXHALE_VOICE_AUDIO_URL);
      exhaleVoiceAudio.preload = 'auto';
      exhaleVoiceAudio.crossOrigin = 'anonymous';
      exhaleVoiceAudio.volume = 0.7;
    }
    if (!ambientAudio && AMBIENT_AUDIO_URL) {
      ambientAudio = new Audio(AMBIENT_AUDIO_URL);
      ambientAudio.preload = 'auto';
      ambientAudio.crossOrigin = 'anonymous';
      // gentle background level and loop
      ambientAudio.loop = true;
      ambientAudio.volume = 0.25;
    }
  } catch (e) {
    // Non-fatal if audio cannot be created in some environments
    // eslint-disable-next-line no-console
    console.warn('gameLogic: failed to create audio elements', e);
  }
}

async function playAmbientSound(): Promise<void> {
  ensureAudioElements();
  if (!ambientAudio) return;
  try {
    ambientAudio.currentTime = 0;
    const p = ambientAudio.play();
    if (p && typeof p.then === 'function') {
      await p.catch(() => {});
    }
  } catch (e) {
    // ignore autoplay failures
    // eslint-disable-next-line no-console
    console.warn('gameLogic: ambient audio play blocked', e);
  }
}

function pauseAmbientSound(): void {
  if (!ambientAudio) return;
  try {
    ambientAudio.pause();
  } catch (e) {
    void e;
  }
}

function stopAmbientSound(): void {
  if (!ambientAudio) return;
  try {
    ambientAudio.pause();
    ambientAudio.currentTime = 0;
  } catch (e) {
    void e;
  }
}

async function playSound(audioEl: HTMLAudioElement | null) {
  if (!audioEl) return;
  try {
    // stop and rewind to ensure a clean start
    audioEl.pause();
    audioEl.currentTime = 0;
    const p = audioEl.play();
    if (p && typeof p.then === 'function') {
      await p.catch((err) => {
        // Autoplay may be blocked until user interaction — ignore errors
        // eslint-disable-next-line no-console
        console.warn('gameLogic: audio play blocked', err);
      });
    }
  } catch (e) {
    // Non-fatal
    // eslint-disable-next-line no-console
    console.warn('gameLogic: audio play failed', e);
  }
}

function playInhaleSound() {
  ensureAudioElements();
  // play both ambient music and voice (if available)
  void playSound(inhaleAudio);
  if (voiceAudioEnabled) {
    void playSound(inhaleVoiceAudio);
  } else {
    stopAndResetAudio(inhaleVoiceAudio);
  }
}

function playExhaleSound() {
  ensureAudioElements();
  void playSound(exhaleAudio);
  if (voiceAudioEnabled) {
    void playSound(exhaleVoiceAudio);
  } else {
    stopAndResetAudio(exhaleVoiceAudio);
  }
}

// ============================================================================
// BREATHING PHASE STATE MACHINE
// ============================================================================
// The breathing cycle follows this order:
// INHALE → HOLD_AFTER_INHALE → EXHALE → HOLD_AFTER_EXHALE → repeat
// All phases exist even if their duration is 0 seconds.

export type BreathingPhase =
  | 'inhale'
  | 'holdAfterInhale'
  | 'exhale'
  | 'holdAfterExhale';

interface MissedTimingEvent {
  timestamp: number;
  phase: string;
  reason: string;
}

interface FeedbackEvent {
  timestamp: number;
  type: 'success' | 'failure';
  reason?: string | null;
}

interface BreathingState {
  /** Current phase in the breathing cycle */
  currentPhase: BreathingPhase;
  /** Timer for the current phase (seconds elapsed) */
  phaseTimer: number;
  /** Whether the breathing system is active */
  isActive: boolean;
  /** Total completed breath cycles */
  cyclesCompleted: number;
  /** Whether the current phase was completed successfully */
  phaseSuccess: boolean;
  /** Whether the last inhale was completed successfully (for reward) */
  lastInhaleSuccess: boolean;
  /** Whether the last exhale was completed successfully (for reward) */
  lastExhaleSuccess: boolean;
  /** Whether user is currently attempting inhale (pressing input) */
  isAttemptingInhale: boolean;
  /** Last recorded failure reason (for debugging) */
  lastFailureReason: string | null;
  /** Whether user is currently following the correct pattern */
  isFollowingPattern: boolean;
  /** Whether the current cycle has had any timing misses */
  currentCycleValid: boolean;
  /** Log of all missed timing events */
  missedTimingEvents: MissedTimingEvent[];
  /** Last feedback event for debug panel */
  lastFeedbackEvent: FeedbackEvent | null;
  /** Whether a failure penalty was already applied during this cycle */
  penaltyAppliedThisCycle: boolean;
}

const breathingState: BreathingState = {
  currentPhase: 'inhale',
  phaseTimer: 0,
  isActive: false,
  cyclesCompleted: 0,
  phaseSuccess: true,
  lastInhaleSuccess: false,
  lastExhaleSuccess: true, // Start true so first inhale can succeed
  isAttemptingInhale: false,
  lastFailureReason: null,
  isFollowingPattern: true,
  currentCycleValid: true,
  missedTimingEvents: [],
  lastFeedbackEvent: null,
  penaltyAppliedThisCycle: false,
};

// ============================================================================
// PHASE HELPERS
// ============================================================================
// Functions to get phase duration and transition to next phase.

/**
 * Get the duration for a specific phase from the breathing pattern.
 */
function getPhaseDuration(phase: BreathingPhase): number {
  switch (phase) {
    case 'inhale':
      return breathingPattern.inhale;
    case 'holdAfterInhale':
      return breathingPattern.holdAfterInhale;
    case 'exhale':
      return breathingPattern.exhale;
    case 'holdAfterExhale':
      return breathingPattern.holdAfterExhale;
  }
}

/**
 * Get the next phase in the breathing cycle.
 * The cycle order is always: inhale → holdAfterInhale → exhale → holdAfterExhale → inhale...
 */
function getNextPhase(phase: BreathingPhase): BreathingPhase {
  switch (phase) {
    case 'inhale':
      return 'holdAfterInhale';
    case 'holdAfterInhale':
      return 'exhale';
    case 'exhale':
      return 'holdAfterExhale';
    case 'holdAfterExhale':
      return 'inhale';
  }
}

/**
 * Check if rising movement is allowed in the current phase.
 * Rising is only allowed during the inhale phase.
 */
function isRisingAllowed(): boolean {
  return breathingState.currentPhase === 'inhale';
}

/**
 * Check if falling movement is allowed in the current phase.
 * Falling is only allowed during the exhale phase.
 */
function isFallingAllowed(): boolean {
  return breathingState.currentPhase === 'exhale';
}

/**
 * Check if we're in a hold phase (no movement allowed).
 */
function isInHoldPhase(): boolean {
  return (
    breathingState.currentPhase === 'holdAfterInhale' ||
    breathingState.currentPhase === 'holdAfterExhale'
  );
}

// ============================================================================
// PHASE TRANSITION LOGIC
// ============================================================================
// Handles transitioning between breathing phases.

/**
 * Transition to the next phase in the breathing cycle.
 * Updates the state machine and triggers appropriate movement changes.
 */
function transitionToNextPhase(): void {
  const previousPhase = breathingState.currentPhase;
  const nextPhase = getNextPhase(previousPhase);

  // Handle phase completion success tracking
  if (previousPhase === 'inhale') {
    // Check if user was actually holding during inhale - if not, it's a failure
    if (!breathingState.isAttemptingInhale && !inputState.isPressed) {
      // User didn't hold during inhale phase
      recordBreathingFailure('Missed timing: no input during inhale');
      breathingState.lastInhaleSuccess = false;
    } else {
      // Inhale completed successfully - mark it
      breathingState.lastInhaleSuccess = breathingState.phaseSuccess;
    }

    // Award rising movement ONLY if inhale was successful AND previous exhale was successful
    if (breathingState.lastInhaleSuccess && breathingState.lastExhaleSuccess) {
      // eslint-disable-next-line no-console
      console.debug('breathingState: inhale SUCCESS - Buddha rises!');
      startRising();
      // The rising will be stopped when we enter hold phase
    } else {
      // eslint-disable-next-line no-console
      console.debug(
        'breathingState: inhale failed or previous exhale failed - no rise'
      );
    }
  } else if (previousPhase === 'exhale') {
    // Exhale completed successfully - mark it
    breathingState.lastExhaleSuccess = breathingState.phaseSuccess;

    if (breathingState.lastExhaleSuccess) {
      // eslint-disable-next-line no-console
      console.debug('breathingState: exhale SUCCESS');
    }
  }

  // Reset the timer for the new phase
  breathingState.phaseTimer = 0;
  breathingState.currentPhase = nextPhase;
  breathingState.phaseSuccess = true;
  // Reset following pattern at phase transition
  breathingState.isFollowingPattern = true;

  // Track completed cycles (when we loop back to inhale from holdAfterExhale)
  if (previousPhase === 'holdAfterExhale' && nextPhase === 'inhale') {
    breathingState.cyclesCompleted++;

    // Award progress for completing a full cycle successfully
    if (breathingState.currentCycleValid) {
      onCycleSuccess();
      awardCycleProgress();
    } else {
      // Cycle had timing misses - apply penalty (once per cycle)
      if (!breathingState.penaltyAppliedThisCycle) {
        applyFailurePenalty();
        breathingState.penaltyAppliedThisCycle = true;
      }
    }

    // Reset cycle validity for the new cycle
    breathingState.currentCycleValid = true;
    breathingState.penaltyAppliedThisCycle = false;
  }

  // Update movement based on the new phase
  updateMovementForPhase(nextPhase);

  // Play audio for phase start if available
  try {
    if (nextPhase === 'inhale') playInhaleSound();
    else if (nextPhase === 'exhale') playExhaleSound();
  } catch (e) {
    void e;
  }

  // eslint-disable-next-line no-console
  console.debug(
    `breathingState: phase transition ${previousPhase} → ${nextPhase}`
  );
}

/**
 * Update the Buddha's movement based on the current phase.
 */
function updateMovementForPhase(phase: BreathingPhase): void {
  switch (phase) {
    case 'inhale':
      // Inhale phase: stop any falling, await user input to attempt inhale
      // Rising is only awarded on inhale completion, not during
      stopMovement();
      break;
    case 'exhale':
      // Exhale phase: Buddha should fall during exhale
      // This represents the release of breath
      startFalling();
      break;
    case 'holdAfterInhale':
    case 'holdAfterExhale':
      // Hold phases: stop all movement
      stopMovement();
      break;
  }
}

// ============================================================================
// BREATHING UPDATE LOOP
// ============================================================================
// Called every frame to update the breathing state machine.

/**
 * Update the breathing system.
 * Should be called every frame with delta time.
 * @param dt - Delta time in seconds since last frame
 */
export function updateBreathing(dt: number): void {
  if (!breathingState.isActive || !isGamePlaying()) return;

  // Update the phase timer
  breathingState.phaseTimer += dt;

  // Get the current phase duration
  const phaseDuration = getPhaseDuration(breathingState.currentPhase);

  // Check if the phase should complete
  // For zero-duration phases, we still transition immediately
  if (breathingState.phaseTimer >= phaseDuration) {
    // Phase completed - transition to the next phase
    transitionToNextPhase();

    // Handle zero-duration phases: keep transitioning until we hit a phase with duration > 0
    // This ensures phases with 0 duration are still visited but don't block the cycle
    let safetyCounter = 0;
    while (
      getPhaseDuration(breathingState.currentPhase) <= 0 &&
      safetyCounter < 4
    ) {
      transitionToNextPhase();
      safetyCounter++;
    }
  }
}

/**
 * Check if the current inhale phase has been completed successfully.
 * Success means the timer has reached or exceeded the pattern's inhale duration.
 */
export function isInhaleComplete(): boolean {
  return (
    breathingState.currentPhase === 'holdAfterInhale' ||
    breathingState.currentPhase === 'exhale' ||
    breathingState.currentPhase === 'holdAfterExhale' ||
    breathingState.phaseTimer >= breathingPattern.inhale
  );
}

/**
 * Check if the current exhale phase has been completed successfully.
 * Success means the timer has reached or exceeded the pattern's exhale duration.
 */
export function isExhaleComplete(): boolean {
  return (
    breathingState.currentPhase === 'holdAfterExhale' ||
    breathingState.currentPhase === 'inhale' ||
    breathingState.currentPhase === 'holdAfterInhale' ||
    breathingState.phaseTimer >= breathingPattern.exhale
  );
}

// ============================================================================
// SUCCESS/FAIL CONDITIONS
// ============================================================================
// Functions to check breathing success/failure based on pattern values.

/**
 * Record a breathing failure.
 * @param reason - Description of why the breathing failed
 */
export function recordBreathingFailure(reason: string): void {
  breathingState.phaseSuccess = false;
  breathingState.lastFailureReason = reason;
  breathingState.isFollowingPattern = false;
  breathingState.currentCycleValid = false;

  // Add to missed timing events log
  const event: MissedTimingEvent = {
    timestamp: Date.now(),
    phase: breathingState.currentPhase,
    reason: reason,
  };
  breathingState.missedTimingEvents.push(event);

  // Keep only the last 50 events to prevent memory issues
  if (breathingState.missedTimingEvents.length > 50) {
    breathingState.missedTimingEvents.shift();
  }

  // Trigger immediate calming failure feedback
  onCycleFail(reason);

  // eslint-disable-next-line no-console
  console.debug(`breathingState: failure recorded - ${reason}`);
}

function handleMistimedWindowInput(reason: string): void {
  // Existing failure feedback + mark cycle invalid
  recordBreathingFailure(reason);

  // Apply immediate downward drift/penalty (but don't stack multiple times per cycle)
  if (!breathingState.penaltyAppliedThisCycle) {
    applyFailurePenalty();
    breathingState.penaltyAppliedThisCycle = true;
  }
}

export function onCycleSuccess(): void {
  breathingState.lastFeedbackEvent = {
    timestamp: Date.now(),
    type: 'success',
    reason: null,
  };

  // Track only perfect cycles that completed without timing misses
  progressState.successfulCycles = Math.max(
    0,
    progressState.successfulCycles + 1
  );

  pulseRing();
  pulseAura();
}

export function onCycleFail(reason: string): void {
  breathingState.lastFeedbackEvent = {
    timestamp: Date.now(),
    type: 'failure',
    reason,
  };

  dimRing();
  dimAura();
  softShakeBuddha();
}

/**
 * Check if the user released too early during inhale.
 * Failure occurs if they release before completing the required inhale duration.
 */
export function checkInhaleReleasedTooEarly(): boolean {
  if (breathingState.currentPhase !== 'inhale') return false;
  return breathingState.phaseTimer < breathingPattern.inhale;
}

/**
 * Check if the user released too early during exhale.
 * Failure occurs if they press again before completing the required exhale duration.
 */
export function checkExhaleInterruptedTooEarly(): boolean {
  if (breathingState.currentPhase !== 'exhale') return false;
  return breathingState.phaseTimer < breathingPattern.exhale;
}

/**
 * Check if the user is trying to perform an incorrect action for the current phase.
 * @param isPressingInput - Whether the user is currently pressing/holding input
 */
export function checkIncorrectPhaseAction(isPressingInput: boolean): boolean {
  // During hold phases, any input change is incorrect if we strictly enforce holds
  // For now, we allow input during holds but movement is blocked
  if (isInHoldPhase()) {
    return false; // No failure for input during holds (movement is already blocked)
  }

  // During inhale, releasing is incorrect until phase is complete
  if (breathingState.currentPhase === 'inhale' && !isPressingInput) {
    return checkInhaleReleasedTooEarly();
  }

  // During exhale, pressing is incorrect until phase is complete
  if (breathingState.currentPhase === 'exhale' && isPressingInput) {
    return checkExhaleInterruptedTooEarly();
  }

  return false;
}

// ============================================================================
// BREATHING CONTROL FUNCTIONS
// ============================================================================
// Functions to start, stop, and reset the breathing system.

/**
 * Start the breathing system.
 * Initializes the state machine at the beginning of the inhale phase.
 */
export function startBreathing(): void {
  breathingState.isActive = true;
  breathingState.currentPhase = 'inhale';
  breathingState.phaseTimer = 0;
  breathingState.cyclesCompleted = 0;
  breathingState.phaseSuccess = true;
  breathingState.lastInhaleSuccess = false;
  breathingState.lastExhaleSuccess = true; // Start true so first inhale can succeed
  breathingState.isAttemptingInhale = false;
  breathingState.lastFailureReason = null;
  breathingState.isFollowingPattern = true;
  breathingState.currentCycleValid = true;
  breathingState.missedTimingEvents = [];
  breathingState.lastFeedbackEvent = null;
  breathingState.penaltyAppliedThisCycle = false;
  // eslint-disable-next-line no-console
  console.debug('breathingState: breathing started');
  // Play inhale audio for the start of the breathing cycle
  try {
    playInhaleSound();
  } catch (e) {
    void e;
  }
}

/**
 * Stop the breathing system.
 */
export function stopBreathing(): void {
  breathingState.isActive = false;
  stopMovement();
  // eslint-disable-next-line no-console
  console.debug('breathingState: breathing stopped');
}

function resumeBreathing(): void {
  breathingState.isActive = true;
  breathingState.isAttemptingInhale = false;
  // eslint-disable-next-line no-console
  console.debug('breathingState: breathing resumed');
}

/**
 * Reset the breathing system to initial state.
 */
export function resetBreathing(): void {
  breathingState.isActive = false;
  breathingState.currentPhase = 'inhale';
  breathingState.phaseTimer = 0;
  breathingState.cyclesCompleted = 0;
  breathingState.phaseSuccess = true;
  breathingState.lastInhaleSuccess = false;
  breathingState.lastExhaleSuccess = true;
  breathingState.isAttemptingInhale = false;
  breathingState.lastFailureReason = null;
  breathingState.isFollowingPattern = true;
  breathingState.currentCycleValid = true;
  breathingState.missedTimingEvents = [];
  breathingState.lastFeedbackEvent = null;
  breathingState.penaltyAppliedThisCycle = false;
  // eslint-disable-next-line no-console
  console.debug('breathingState: breathing reset');
}

/**
 * Get the current breathing state (for debugging/UI).
 */
export function getBreathingState(): {
  pattern: BreathingPattern;
  currentPhase: BreathingPhase;
  phaseTimer: number;
  phaseDuration: number;
  phaseProgress: number;
  validWindowStart: number;
  validWindowEnd: number;
  validWindowActive: boolean;
  isActive: boolean;
  cyclesCompleted: number;
  phaseSuccess: boolean;
  lastInhaleSuccess: boolean;
  lastExhaleSuccess: boolean;
  isAttemptingInhale: boolean;
  lastFailureReason: string | null;
  isFollowingPattern: boolean;
  currentCycleValid: boolean;
} {
  const phaseDuration = getPhaseDuration(breathingState.currentPhase);
  const phaseProgress =
    phaseDuration > 0
      ? Math.min(breathingState.phaseTimer / phaseDuration, 1)
      : 1;
  const validWindowEnd = getValidWindowEndRatio(breathingState.currentPhase);
  return {
    pattern: { ...breathingPattern },
    currentPhase: breathingState.currentPhase,
    phaseTimer: breathingState.phaseTimer,
    phaseDuration,
    phaseProgress,
    validWindowStart: 0,
    validWindowEnd,
    validWindowActive:
      breathingState.isActive &&
      isGamePlaying() &&
      (breathingState.currentPhase === 'inhale' ||
        breathingState.currentPhase === 'exhale')
        ? phaseProgress <= validWindowEnd
        : false,
    isActive: breathingState.isActive,
    cyclesCompleted: breathingState.cyclesCompleted,
    phaseSuccess: breathingState.phaseSuccess,
    lastInhaleSuccess: breathingState.lastInhaleSuccess,
    lastExhaleSuccess: breathingState.lastExhaleSuccess,
    isAttemptingInhale: breathingState.isAttemptingInhale,
    lastFailureReason: breathingState.lastFailureReason,
    isFollowingPattern: breathingState.isFollowingPattern,
    currentCycleValid: breathingState.currentCycleValid,
  };
}

/**
 * Get debug information for the debug panel.
 */
export function getDebugInfo(): {
  missedTimingEvents: MissedTimingEvent[];
  currentCycleValid: boolean;
  isFollowingPattern: boolean;
  inputEvents: {
    timestamp: number;
    type: 'start' | 'end';
    source: 'mouse' | 'touch' | 'key';
    key?: string | null;
  }[];
  lastFeedbackEvent: FeedbackEvent | null;
  phaseProgress: number;
  validWindowStart: number;
  validWindowEnd: number;
} {
  return {
    missedTimingEvents: [...breathingState.missedTimingEvents],
    currentCycleValid: breathingState.currentCycleValid,
    isFollowingPattern: breathingState.isFollowingPattern,
    inputEvents: [...inputState.events],
    lastFeedbackEvent: breathingState.lastFeedbackEvent,
    phaseProgress: getCurrentPhaseProgressRatio(),
    validWindowStart: 0,
    validWindowEnd: getValidWindowEndRatio(breathingState.currentPhase),
  };
}

/**
 * Set a new breathing pattern.
 * This allows switching between different breathing ratios at runtime.
 * @param pattern - The new breathing pattern to use
 */
export function setBreathingPattern(pattern: BreathingPattern): void {
  breathingPattern = { ...pattern };
  // Reset the breathing system when pattern changes
  resetBreathing();
  // eslint-disable-next-line no-console
  console.debug(
    `breathingState: pattern set to ${pattern.inhale}-${pattern.holdAfterInhale}-${pattern.exhale}-${pattern.holdAfterExhale}`
  );
}

/**
 * Get the current breathing pattern.
 */
export function getBreathingPattern(): BreathingPattern {
  return { ...breathingPattern };
}

// ============================================================================
// PROGRESS & GOAL FUNCTIONS
// ============================================================================
// Functions to manage progress toward the cloud goal.

/**
 * Award progress for completing a successful breath cycle.
 * Called when a full inhale-hold-exhale-hold cycle completes successfully.
 */
export function awardCycleProgress(): void {
  if (progressState.status !== 'playing') return;

  const previousProgress = progressState.currentProgress;
  // Increment the consecutive success streak then compute streak bonus
  progressState.currentSuccessStreak =
    (progressState.currentSuccessStreak || 0) + 1;
  const streakBonus = Math.min(
    progressState.currentSuccessStreak * goalConfig.streakBonusPerSuccess,
    goalConfig.maxStreakBonus
  );
  const gain = goalConfig.progressPerCycle + streakBonus;

  progressState.currentProgress = progressState.currentProgress + gain;

  // eslint-disable-next-line no-console
  console.debug(
    `progressState: cycle complete, progress ${previousProgress.toFixed(2)} → ${progressState.currentProgress.toFixed(2)}`
  );

  if (progressState.onCycleComplete) {
    progressState.onCycleComplete();
  }
}

/**
 * Apply a penalty for breathing failure.
 * Gently reduces progress but never below the minimum.
 */
export function applyFailurePenalty(): void {
  if (progressState.status !== 'playing') return;

  const previousProgress = progressState.currentProgress;
  progressState.currentProgress = Math.max(
    progressState.currentProgress - goalConfig.penaltyOnFailure,
    goalConfig.minProgress
  );

  // Reset consecutive success streak on failure
  progressState.currentSuccessStreak = 0;

  // eslint-disable-next-line no-console
  console.debug(
    `progressState: failure penalty, progress ${previousProgress.toFixed(2)} → ${progressState.currentProgress.toFixed(2)}`
  );

  if (progressState.onFailure) {
    progressState.onFailure();
  }
}

/**
 * Trigger the win condition.
 * Called when the Buddha reaches the goal height.
 */
// Win condition no longer driven by goal height – Scene will detect session
// completion by Buddha exiting the top of the screen. The progress system
// now only tracks upward movement per successful cycle and penalties.

/**
 * Get the current progress as a percentage (0-1).
 */
export function getProgressPercent(): number {
  if (goalConfig.goalHeight <= 0) return 0;
  return Math.min(progressState.currentProgress / goalConfig.goalHeight, 1);
}

/**
 * Get the current progress state.
 */
export function getProgressState(): {
  currentProgress: number;
  goalHeight: number;
  elapsedSeconds: number;
  progressPercent: number;
  hasWon: boolean;
  status: GameStatus;
  /** Current consecutive cycle success streak */
  currentSuccessStreak: number;
  /** Total perfect inhale+exhale cycles completed */
  successfulCycles: number;
} {
  return {
    currentProgress: progressState.currentProgress,
    goalHeight: goalConfig.goalHeight,
    elapsedSeconds: progressState.startTime
      ? progressState.accumulatedElapsed +
        Math.max(0, (Date.now() - progressState.startTime) / 1000)
      : progressState.accumulatedElapsed,
    progressPercent: getProgressPercent(),
    hasWon: progressState.hasWon,
    status: progressState.status,
    currentSuccessStreak: progressState.currentSuccessStreak,
    successfulCycles: progressState.successfulCycles,
  };
}

/**
 * Set the goal configuration.
 * @param config - Partial goal configuration to merge with defaults
 */
export function setGoalConfig(config: Partial<GoalConfig>): void {
  goalConfig = { ...goalConfig, ...config };
  // eslint-disable-next-line no-console
  console.debug(`progressState: goal config updated`, goalConfig);
}

/**
 * Get the current goal configuration.
 */
export function getGoalConfig(): GoalConfig {
  return { ...goalConfig };
}

export interface LevelBehaviorConfig {
  breathingPattern: BreathingPattern;
  penaltyMultiplier?: number;
  voiceAudioEnabled?: boolean;
  strictHoldTiming?: boolean;
  inputWindowRatioMultiplier?: number;
  goalOverrides?: Partial<GoalConfig>;
}

export function configureLevel(settings: LevelBehaviorConfig): void {
  const penaltyBase =
    settings.goalOverrides?.penaltyOnFailure ??
    DEFAULT_GOAL_CONFIG.penaltyOnFailure;

  const appliedGoalConfig: GoalConfig = {
    ...DEFAULT_GOAL_CONFIG,
    ...settings.goalOverrides,
    penaltyOnFailure: penaltyBase * (settings.penaltyMultiplier ?? 1),
  };

  setGoalConfig(appliedGoalConfig);
  setBreathingPattern(settings.breathingPattern);
  setVoiceAudioEnabledSetting(settings.voiceAudioEnabled ?? true);
  setHoldPhaseStrictness(settings.strictHoldTiming ?? false);
  inputWindowRatioMultiplier = settings.inputWindowRatioMultiplier ?? 1.0;
}

/**
 * Set callback functions for game events.
 */
export function setGameCallbacks(callbacks: {
  onCycleComplete?: () => void;
  onWin?: () => void;
  onFailure?: () => void;
}): void {
  if (callbacks.onCycleComplete !== undefined) {
    progressState.onCycleComplete = callbacks.onCycleComplete;
  }
  if (callbacks.onWin !== undefined) {
    progressState.onWin = callbacks.onWin;
  }
  if (callbacks.onFailure !== undefined) {
    progressState.onFailure = callbacks.onFailure;
  }
}

/**
 * Start the game.
 * Initializes progress and breathing systems.
 */
export function startGame(): void {
  progressState.status = 'playing';
  progressState.hasWon = false;
  progressState.currentProgress = 0;
  progressState.successfulCycles = 0;
  progressState.currentSuccessStreak = 0;
  inputState.isPressed = false;
  breathingState.isAttemptingInhale = false;
  startBreathing();
  progressState.startTime = Date.now();
  progressState.accumulatedElapsed = 0;
  // start ambient background audio
  void playAmbientSound();
  // eslint-disable-next-line no-console
  console.debug('progressState: game started');
}

/**
 * Pause the game.
 */
export function pauseGame(): void {
  if (progressState.status !== 'playing') return;

  progressState.status = 'paused';
  breathingState.isAttemptingInhale = false;
  inputState.isPressed = false;
  stopBreathing();
  stopMovement();
  // pause ambient background audio
  pauseAmbientSound();
  // accumulate elapsed time and freeze timer
  if (progressState.startTime) {
    progressState.accumulatedElapsed +=
      (Date.now() - progressState.startTime) / 1000;
    progressState.startTime = null;
  }
  // eslint-disable-next-line no-console
  console.debug('progressState: game paused');
}

/**
 * Resume the game.
 */
export function resumeGame(): void {
  if (progressState.status !== 'paused') return;

  progressState.status = 'playing';
  resumeBreathing();
  // resume ambient background audio
  void playAmbientSound();
  // resume timer
  if (!progressState.startTime) progressState.startTime = Date.now();
  // eslint-disable-next-line no-console
  console.debug('progressState: game resumed');
}

/**
 * Reset the game to initial state.
 */
export function resetGame(): void {
  progressState.currentProgress = 0;
  progressState.hasWon = false;
  progressState.currentSuccessStreak = 0;
  progressState.successfulCycles = 0;
  progressState.status = 'idle';
  inputState.isPressed = false;
  breathingState.isAttemptingInhale = false;
  resetBreathing();
  progressState.startTime = null;
  progressState.accumulatedElapsed = 0;
  // ensure ambient audio is stopped on reset
  stopAmbientSound();
  // eslint-disable-next-line no-console
  console.debug('progressState: game reset');
}

/**
 * Check if the game has been won.
 */
export function hasWon(): boolean {
  return progressState.hasWon;
}

/**
 * Get the current game status.
 */
export function getGameStatus(): GameStatus {
  return progressState.status;
}

// ============================================================================
// INPUT HANDLERS
// ============================================================================
// Handle mouse and touch events to control Buddha's rising and falling.
// Movement is now phase-driven: rising only during inhale, falling only during exhale.

/**
 * Handle the start of user input (mouse down or touch start).
 * This signals the user is ATTEMPTING to inhale.
 * Rising only happens if the inhale phase completes successfully.
 */
function handleInputStart(
  event: MouseEvent | TouchEvent,
  source: 'mouse' | 'touch' | 'key' = 'mouse',
  keyName?: string | null
) {
  if (!isGamePlaying()) return;

  // Prevent default to avoid unwanted scrolling on mobile
  event.preventDefault();

  if (inputState.isPressed) return; // Already pressed, ignore
  inputState.isPressed = true;
  breathingState.isAttemptingInhale = true;

  // log input start
  try {
    inputState.events.unshift({
      timestamp: Date.now(),
      type: 'start',
      source,
      key: keyName ?? null,
    });
    if (inputState.events.length > 50) inputState.events.pop();
  } catch (e) {
    void e;
  }

  // Check if we're in the correct phase for inhaling
  if (isRisingAllowed()) {
    // User started pressing during inhale phase.
    // Only accept input near the start of the phase.
    const progress = getCurrentPhaseProgressRatio();
    const windowEnd = getValidWindowEndRatio('inhale');

    if (progress > windowEnd) {
      handleMistimedWindowInput('Missed timing: inhale press too late');
      // Allow the player to keep holding — no lockout.
      // eslint-disable-next-line no-console
      console.debug(
        'gameLogic: input START during INHALE phase - TOO LATE for valid window'
      );
      return;
    }

    breathingState.isFollowingPattern = true;
    // eslint-disable-next-line no-console
    console.debug('gameLogic: input START during INHALE phase - within window');
  } else if (isInHoldPhase()) {
    if (strictHoldTimingEnabled) {
      // Pressing during hold phase - this is incorrect timing!
      recordBreathingFailure('Missed timing: clicked during hold');
      // eslint-disable-next-line no-console
      console.debug('gameLogic: input START during HOLD phase - INCORRECT');
    }
    return;
  } else {
    // Pressing during exhale phase - this is incorrect timing!
    if (checkExhaleInterruptedTooEarly()) {
      recordBreathingFailure('Missed timing: held during exhale');
      breathingState.lastExhaleSuccess = false;
      // Trigger falling as penalty
      startFalling();
    }
  }
}

/**
 * Handle the end of user input (mouse up or touch end).
 * This signals the user is ATTEMPTING to exhale (releasing breath).
 */
function handleInputEnd(
  event: MouseEvent | TouchEvent,
  source: 'mouse' | 'touch' | 'key' = 'mouse',
  keyName?: string | null
) {
  if (!isGamePlaying()) return;

  // Prevent default to avoid unwanted behavior
  event.preventDefault();

  if (!inputState.isPressed) return; // Not pressed, ignore
  inputState.isPressed = false;
  breathingState.isAttemptingInhale = false;

  // log input end
  try {
    inputState.events.unshift({
      timestamp: Date.now(),
      type: 'end',
      source,
      key: keyName ?? null,
    });
    if (inputState.events.length > 50) inputState.events.pop();
  } catch (e) {
    void e;
  }

  // Check if we're in the correct phase for exhaling (releasing)
  if (isFallingAllowed()) {
    // User released during exhale phase.
    // Only accept release near the start of the phase.
    const progress = getCurrentPhaseProgressRatio();
    const windowEnd = getValidWindowEndRatio('exhale');

    if (progress > windowEnd) {
      handleMistimedWindowInput('Missed timing: exhale release too late');
      // Allow recovery next cycle — no lockout.
      // eslint-disable-next-line no-console
      console.debug(
        'gameLogic: input END during EXHALE phase - TOO LATE for valid window'
      );
      return;
    }

    breathingState.isFollowingPattern = true;
    // eslint-disable-next-line no-console
    console.debug('gameLogic: input END during EXHALE phase - within window');
  } else if (isInHoldPhase()) {
    if (strictHoldTimingEnabled) {
      // Releasing during hold phase - this is incorrect timing!
      recordBreathingFailure('Missed timing: released during hold');
      // eslint-disable-next-line no-console
      console.debug('gameLogic: input END during HOLD phase - INCORRECT');
    }
    return;
  } else {
    // Releasing during inhale phase - this is incorrect (released too early)!
    if (checkInhaleReleasedTooEarly()) {
      recordBreathingFailure('Missed timing: released too early on inhale');
      breathingState.lastInhaleSuccess = false;
      breathingState.phaseSuccess = false;
      // Trigger falling as penalty for failing inhale
      startFalling();
    }
  }
}

/**
 * Handle touch cancel (e.g., when a call comes in or gesture is interrupted).
 * Treat as input end.
 */
function handleTouchCancel(event: TouchEvent) {
  handleInputEnd(event, 'touch');
}

/**
 * Handle keyboard key down (space bar for inhale).
 */
function handleKeyDown(event: KeyboardEvent) {
  // Handle space bar: hold to inhale, release to exhale
  const code = event.code;

  // Ignore if user typing into input
  const target = event.target as HTMLElement | null;
  if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
    return;
  }

  if (code === 'Space' || event.key === ' ') {
    if (!isGamePlaying()) return;
    event.preventDefault();
    if (inputState.isPressed) return; // Ignore repeats
    handleInputStart(
      { preventDefault: () => {} } as MouseEvent,
      'key',
      'Space'
    );
    return;
  }
}

/**
 * Handle keyboard key up (space bar release for exhale).
 */
function handleKeyUp(event: KeyboardEvent) {
  const code = event.code;

  // Ignore if user typing into input
  const target = event.target as HTMLElement | null;
  if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
    return;
  }

  if (code === 'Space' || event.key === ' ') {
    if (!isGamePlaying()) return;
    event.preventDefault();
    handleInputEnd({ preventDefault: () => {} } as MouseEvent, 'key', 'Space');
    return;
  }
}

// ============================================================================
// INPUT SETUP & CLEANUP
// ============================================================================
// Functions to set up and tear down input listeners.

/**
 * Set up input listeners for controlling the Buddha's movement.
 * Supports mouse (desktop), touch (mobile), and keyboard (space bar) input.
 *
 * @param container - Optional DOM element to attach listeners to. Defaults to window.
 */
export function setupInputListeners(container?: HTMLElement) {
  if (inputState.initialized) {
    // eslint-disable-next-line no-console
    console.debug('gameLogic: input listeners already initialized');
    return;
  }

  const target: HTMLElement | Window = container ?? window;

  // Mouse events
  const mouseDownHandler = (e: Event) =>
    handleInputStart(e as MouseEvent, 'mouse');
  const mouseUpHandler = (e: Event) => handleInputEnd(e as MouseEvent, 'mouse');

  // Touch events
  const touchStartHandler = (e: Event) =>
    handleInputStart(e as TouchEvent, 'touch');
  const touchEndHandler = (e: Event) =>
    handleInputEnd(e as TouchEvent, 'touch');
  const touchCancelHandler = (e: Event) => handleTouchCancel(e as TouchEvent);

  // Keyboard events (always on window for best capture)
  const keyDownHandler = (e: Event) => handleKeyDown(e as KeyboardEvent);
  const keyUpHandler = (e: Event) => handleKeyUp(e as KeyboardEvent);

  // Add listeners with passive: false to allow preventDefault
  target.addEventListener('mousedown', mouseDownHandler, { passive: false });
  target.addEventListener('mouseup', mouseUpHandler, { passive: false });
  target.addEventListener('touchstart', touchStartHandler, { passive: false });
  target.addEventListener('touchend', touchEndHandler, { passive: false });
  target.addEventListener('touchcancel', touchCancelHandler, {
    passive: false,
  });

  // Keyboard events on window
  window.addEventListener('keydown', keyDownHandler, { passive: false });
  window.addEventListener('keyup', keyUpHandler, { passive: false });

  // Also listen on window for mouseup in case user drags outside the container
  if (target !== window) {
    window.addEventListener('mouseup', mouseUpHandler, { passive: false });
    inputState.cleanupFns.push(() => {
      window.removeEventListener('mouseup', mouseUpHandler);
    });
  }

  // Store cleanup functions
  inputState.cleanupFns.push(() => {
    target.removeEventListener('mousedown', mouseDownHandler);
    target.removeEventListener('mouseup', mouseUpHandler);
    target.removeEventListener('touchstart', touchStartHandler);
    target.removeEventListener('touchend', touchEndHandler);
    target.removeEventListener('touchcancel', touchCancelHandler);
    window.removeEventListener('keydown', keyDownHandler);
    window.removeEventListener('keyup', keyUpHandler);
  });

  inputState.initialized = true;
  // eslint-disable-next-line no-console
  console.debug('gameLogic: input listeners set up (mouse, touch, keyboard)');
}

/**
 * Remove all input listeners.
 * Call this when the game is disposed.
 */
export function cleanupInputListeners() {
  if (!inputState.initialized) return;

  // Run all cleanup functions
  inputState.cleanupFns.forEach((fn) => fn());
  inputState.cleanupFns = [];

  // Reset state
  inputState.initialized = false;
  inputState.isPressed = false;

  // eslint-disable-next-line no-console
  console.debug('gameLogic: input listeners cleaned up');
}

/**
 * Check if input is currently active (pressed/held).
 */
export function isInputActive(): boolean {
  return inputState.isPressed;
}

/**
 * Check if input listeners have been initialized.
 */
export function isInputInitialized(): boolean {
  return inputState.initialized;
}

export default {
  setupInputListeners,
  cleanupInputListeners,
  isInputActive,
  isInputInitialized,
  // Breathing system exports
  updateBreathing,
  startBreathing,
  stopBreathing,
  resetBreathing,
  getBreathingState,
  getDebugInfo,
  setBreathingPattern,
  getBreathingPattern,
  isInhaleComplete,
  isExhaleComplete,
  recordBreathingFailure,
  onCycleSuccess,
  onCycleFail,
  checkInhaleReleasedTooEarly,
  checkExhaleInterruptedTooEarly,
  checkIncorrectPhaseAction,
  // Progress & Goal exports
  awardCycleProgress,
  applyFailurePenalty,
  getProgressPercent,
  getProgressState,
  setGoalConfig,
  getGoalConfig,
  setGameCallbacks,
  markSessionComplete,
  startGame,
  pauseGame,
  resumeGame,
  resetGame,
  hasWon,
  getGameStatus,
  configureLevel,
};
